/**
 * WebGL compositor for the "Mosaic GL" experiments render path
 * (mosaicGlController.ts): one draw call, one fullscreen quad, resampling a
 * single R8/LUMINANCE mosaic raster through mosaicGlShader.ts's
 * runtime-toggleable kernel and colorizing via a palette LUT texture
 * (mosaicGlPalette.ts). Mirrors fusionCompositor.ts's shape closely (GL
 * context creation with try/catch fallback, fullscreen-quad position
 * buffer, LUT texture setup, mustGetUniform) but for exactly one source
 * texture instead of up to N station slots — there's no blend rule here,
 * only a resampling kernel, so the per-station machinery doesn't apply.
 *
 * ALSO owns the GPU field-smoothing pipeline (mosaicGlSmoothShader.ts's
 * despeckle + separable-exact-Gaussian passes, run via FBO ping-pong into
 * RGBA16F/half-float render targets) — the PRIMARY smoothing path,
 * effectively free per frame, used whenever `gpuSmoothingAvailable` is
 * true (probed once at construction, mosaicGlCapabilities.ts's pure
 * decision over real capability facts). mosaicGlFieldSmoothing.ts's CPU
 * worker path remains the fallback mosaicGlController.ts uses when it
 * isn't — this file never assumes GPU smoothing works; every code path
 * here degrades to "just sample the raw texture directly" if it doesn't.
 */
import { compileProgram, buildMosaicGlFragmentShaderSource, VERTEX_SHADER_SOURCE } from "./mosaicGlShader";
import { SAMPLING_MODE_INDEX, FLOOR_ORDER_INDEX, FLOOR_HARDNESS_INDEX } from "./mosaicGlShader";
import { buildBandedPaletteLut, buildContinuousPaletteLut, BANDED_LUT_SIZE, CONTINUOUS_LUT_SIZE } from "./mosaicGlPalette";
import { buildDespeckleFragmentShaderSource, buildBlurFragmentShaderSource, UNFLIPPED_VERTEX_SHADER_SOURCE } from "./mosaicGlSmoothShader";
import { gaussianKernel1D, padKernelToBlurTapCount, BLUR_TAP_COUNT } from "./mosaicGlFieldSmoothing";
import { decideSmoothingPath, type SmoothingCapabilities } from "./mosaicGlCapabilities";
import { PRECIP_FLOOR_RAW } from "./fusionCompositor";
import { dbzToValue } from "./palette";
import { recordDuration } from "./perfStages";
import type { OverviewLevel, UvTransform } from "./fusionGeo";

export type SamplingMode = "nearest" | "bilinear" | "bicubic";
export const SAMPLING_MODES: readonly SamplingMode[] = ["nearest", "bilinear", "bicubic"];

export type FloorOrder = "after" | "before";
export const FLOOR_ORDERS: readonly FloorOrder[] = ["after", "before"];

/** How the "precip only" floor cutoff turns into alpha, orthogonal to
 * FloorOrder above (which controls WHERE in the kernel the floor acts, only
 * meaningful for the unsmoothed nodata-aware path — see mosaicGlShader.ts's
 * doc comment). "hard" is a binary transparent/opaque step (the original
 * behavior); "feathered" is a 3 dBZ-wide smoothstep ramp — round-3 study
 * spec, added because a hard cutoff on a SMOOTHED field reintroduced a
 * visible stairstep at the floor boundary that the smoothing was supposed
 * to remove. */
export type FloorHardness = "hard" | "feathered";
export const FLOOR_HARDNESS_MODES: readonly FloorHardness[] = ["feathered", "hard"];

export type PaletteMode = "banded" | "continuous";
export const PALETTE_MODES: readonly PaletteMode[] = ["banded", "continuous"];

/** The feathered floor's ramp width, in raw-byte-normalized units — 3 dBZ
 * per the study spec, converted via palette.ts's own dbzToValue (this
 * client's single source of truth for the dBZ<->raw-byte quantization
 * contract, exactly 2 raw units per dBZ by construction) rather than a
 * hand-typed "6". */
const FEATHER_WIDTH_RAW = dbzToValue(3) - dbzToValue(0);
const FEATHER_WIDTH_NORM = FEATHER_WIDTH_RAW / 255;

/** The raw byte for exactly 0 dBZ (mosaicGlFieldSmoothing.ts's own
 * NO_ECHO_FILL_RAW constant isn't imported directly to avoid a second
 * source of truth drifting — recomputed here the identical way, via
 * palette.ts's dbzToValue, which IS the single source of truth both files
 * defer to). */
const NO_ECHO_FILL_NORM = dbzToValue(0) / 255;

/** The mosaic COG's own IFD dimensions (workers/CONTRACTS.md "Mosaic-head" —
 * IFD0 is the full-res CONUS z7 grid; IFD1 is its 2x-downsample internal
 * overview, same [2, 4] overview convention fusionGeo.ts's OverviewLevel
 * doc comment cites for station COGs). Only referenced here for the
 * MAX_TEXTURE_SIZE safety log below — the actual decoded width/height
 * always come from the real decode result (fusionDecoder.ts), never these
 * constants directly. */
export const MOSAIC_IFD0_WIDTH = 5888;
export const MOSAIC_IFD0_HEIGHT = 3328;
export const MOSAIC_IFD1_WIDTH = 2944;
export const MOSAIC_IFD1_HEIGHT = 1664;

/** Conservative safety margin, not the exact minimum IFD0 needs (5888): a
 * GPU reporting anything less than this comfortably clears IFD1's 2944px
 * width even on implementations far below common desktop/mobile baselines,
 * so there's no need to thread the needle on the literal 5888 requirement. */
const MAX_TEXTURE_SIZE_SAFETY_THRESHOLD = 8192;

/** The LOD to actually decode/upload: the zoom-driven level
 * (fusionGeo.ts's overviewLevelForZoom — same function the CPU mosaic path
 * uses, so map moves/zooms drive the same LOD transitions either way),
 * clamped no finer than IFD1 whenever this GL implementation's
 * MAX_TEXTURE_SIZE can't comfortably fit IFD0 (5888x3328). Pure function of
 * the two inputs so it's unit-testable without a real GL context. */
export function effectiveLodLevel(zoomLevel: OverviewLevel, maxTextureSize: number): OverviewLevel {
  if (maxTextureSize < MAX_TEXTURE_SIZE_SAFETY_THRESHOLD) return Math.max(zoomLevel, 1) as OverviewLevel;
  return zoomLevel;
}

type GL = WebGLRenderingContext | WebGL2RenderingContext;

/** Minimal shape of the disjoint-timer-query extension this file actually
 * uses — covers both `EXT_disjoint_timer_query` (WebGL1) and
 * `EXT_disjoint_timer_query_webgl2` (WebGL2, whose begin/end/delete are the
 * CORE gl.beginQuery/endQuery/deleteQuery instead of *_EXT-suffixed
 * methods — handled by isWebGL2 branches at each call site, not by this
 * type). Not in TS's DOM lib (a non-standard extension), so typed by hand
 * rather than `any` throughout. */
interface DisjointTimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
  QUERY_RESULT_AVAILABLE_EXT?: number;
  QUERY_RESULT_EXT?: number;
  createQueryEXT?: () => WebGLQuery;
  deleteQueryEXT?: (q: WebGLQuery) => void;
  beginQueryEXT?: (target: number, q: WebGLQuery) => void;
  endQueryEXT?: (target: number) => void;
  getQueryObjectEXT?: (q: WebGLQuery, pname: number) => unknown;
}

interface HalfFloatExt {
  HALF_FLOAT_OES: number;
}

export class MosaicGlCompositor {
  readonly canvas: HTMLCanvasElement;
  private gl: GL;
  private readonly isWebGL2: boolean;
  private program: WebGLProgram;
  readonly maxTextureSize: number;
  readonly cappedForTextureSize: boolean;

  private positionBuffer: WebGLBuffer;
  private bandedPaletteTexture: WebGLTexture;
  private continuousPaletteTexture: WebGLTexture;
  private rawTexture: WebGLTexture | null = null;
  private rawDims: { width: number; height: number } | null = null;

  private uTex: WebGLUniformLocation;
  private uTexSize: WebGLUniformLocation;
  private uUvXform: WebGLUniformLocation;
  private uSampling: WebGLUniformLocation;
  private uFloorOrder: WebGLUniformLocation;
  private uFloor: WebGLUniformLocation;
  private uFloorHard: WebGLUniformLocation;
  private uFeatherWidth: WebGLUniformLocation;
  private uPalette: WebGLUniformLocation;
  private uPaletteSize: WebGLUniformLocation;
  private aPositionMain: number;

  // ---- GPU field-smoothing pipeline (mosaicGlSmoothShader.ts) ----
  /** True only once verified via an EMPIRICAL FBO-completeness probe at
   * construction (mosaicGlCapabilities.ts's decideSmoothingPath) — never
   * assumed from extension presence alone. mosaicGlController.ts checks
   * this before ever calling smoothOnGpu(); every method below still
   * no-ops defensively if it's false, so a caller bug here degrades to
   * "no smoothing" rather than a GL error. */
  readonly gpuSmoothingAvailable: boolean;
  private despeckleProgram: WebGLProgram | null = null;
  private blurProgram: WebGLProgram | null = null;
  private aPositionDespeckle = 0;
  private aPositionBlur = 0;
  private uDespeckleRawTex!: WebGLUniformLocation;
  private uDespeckleTexSize!: WebGLUniformLocation;
  private uDespeckleFloorNorm!: WebGLUniformLocation;
  private uDespeckleFillNorm!: WebGLUniformLocation;
  private uDespeckleEnabled!: WebGLUniformLocation;
  private uApplyFill!: WebGLUniformLocation;
  private uBlurSrc!: WebGLUniformLocation;
  private uBlurTexelStep!: WebGLUniformLocation;
  private uBlurWeights: WebGLUniformLocation[] = [];
  /** Two-texture ping-pong (mosaicGlCompositor.ts's own doc comment on
   * smoothOnGpu() below has the full pass sequence/reuse rationale). */
  private pingTextures: [WebGLTexture, WebGLTexture] | null = null;
  private pingFbos: [WebGLFramebuffer, WebGLFramebuffer] | null = null;
  private pingDims: { width: number; height: number } | null = null;
  /** Which texture render() should sample: null means "sample rawTexture
   * directly" (smoothing bypassed — sigma=off AND despeckle=false, or GPU
   * smoothing unavailable and the caller is using the CPU-fallback path
   * instead, which uploads its own pre-smoothed grid via setRawTexture()
   * exactly as before this feature existed). */
  private smoothedTexture: WebGLTexture | null = null;

  private timerExt: DisjointTimerExt | null = null;
  private pendingQuery: WebGLQuery | null = null;
  private pendingQueryMeta: string | null = null;
  /** WebGL1 only: `OES_texture_half_float`, queried ONCE during the
   * capability probe and reused everywhere a half-float texture needs
   * creating (ensurePingBuffers, the FBO-completeness probe itself) rather
   * than re-querying `getExtension` at each call site. Always null under
   * WebGL2 (which needs no separate "can this type of texture even be
   * created" extension — see allocateFloatTexture's own doc comment). */
  private halfFloatExt: HalfFloatExt | null = null;

  /** Set (and latched) by the canvas's own "webglcontextlost" listener —
   * checked by mosaicGlController.ts before every render so a lost context
   * mid-session triggers the same CPU-mosaic fallback as never having had a
   * usable context at all, rather than silently drawing nothing. There is
   * no auto-recovery on "webglcontextrestored": every GL object here
   * (program, buffers, textures) is gone with the lost context, and by the
   * time a restore could fire, the controller has already switched the app
   * back to the CPU path — same "construction-time-only" fallback scope as
   * fusionCompositor.ts already has, just also covering the mid-session case. */
  private lost = false;

  constructor() {
    this.canvas = document.createElement("canvas");
    // Explicit (not just relying on the browser default, which is also
    // `true`): the shader's fragment output is premultiplied by design
    // (main() below, "standard premultiplied alpha" — the feathered floor
    // makes fractional alpha common, unlike the old binary hard cutoff
    // where straight vs. premultiplied never differed) — stating the
    // context option explicitly documents that contract at the call site
    // instead of relying on an implicit default staying what it is today.
    const contextOptions: WebGLContextAttributes = { premultipliedAlpha: true };
    // WebGL2 preferred (task: "upgrade to webgl2-with-webgl1-fallback"):
    // renderable float/half-float targets need only ONE extension there
    // (EXT_color_buffer_float) versus WebGL1's two (OES_texture_half_float
    // to create such a texture at all, EXT_color_buffer_half_float to
    // actually render into one) — see mosaicGlCapabilities.ts's doc comment.
    const gl2 = this.canvas.getContext("webgl2", contextOptions) as WebGL2RenderingContext | null;
    const gl1 = gl2 ? null : (this.canvas.getContext("webgl", contextOptions) as WebGLRenderingContext | null);
    const gl = gl2 ?? gl1;
    if (!gl) throw new Error("WebGL unavailable");
    this.gl = gl;
    this.isWebGL2 = !!gl2;
    this.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    this.cappedForTextureSize = this.maxTextureSize < MAX_TEXTURE_SIZE_SAFETY_THRESHOLD;

    this.canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      this.lost = true;
    });

    this.program = compileProgram(gl, VERTEX_SHADER_SOURCE, buildMosaicGlFragmentShaderSource());
    gl.useProgram(this.program);

    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buf = gl.createBuffer();
    if (!buf) throw new Error("gl.createBuffer failed");
    this.positionBuffer = buf;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    this.aPositionMain = gl.getAttribLocation(this.program, "a_position");
    gl.enableVertexAttribArray(this.aPositionMain);
    gl.vertexAttribPointer(this.aPositionMain, 2, gl.FLOAT, false, 0, 0);

    this.bandedPaletteTexture = this.createPaletteTexture(buildBandedPaletteLut(), BANDED_LUT_SIZE, gl.NEAREST);
    this.continuousPaletteTexture = this.createPaletteTexture(buildContinuousPaletteLut(), CONTINUOUS_LUT_SIZE, gl.LINEAR);

    this.uTex = this.mustGetUniform(this.program, "u_tex");
    this.uTexSize = this.mustGetUniform(this.program, "u_texSize");
    this.uUvXform = this.mustGetUniform(this.program, "u_uvXform");
    this.uSampling = this.mustGetUniform(this.program, "u_sampling");
    this.uFloorOrder = this.mustGetUniform(this.program, "u_floorOrder");
    this.uFloor = this.mustGetUniform(this.program, "u_floor");
    this.uFloorHard = this.mustGetUniform(this.program, "u_floorHard");
    this.uFeatherWidth = this.mustGetUniform(this.program, "u_featherWidth");
    this.uPalette = this.mustGetUniform(this.program, "u_palette");
    this.uPaletteSize = this.mustGetUniform(this.program, "u_paletteSize");

    // GPU smoothing capability probe — empirical (create-and-check), not
    // just "the extension string exists" (mosaicGlCapabilities.ts's own
    // doc comment on why). Wrapped in try/catch: ANY failure here — a
    // missing extension, an incomplete FBO, an unexpected exception —
    // degrades to gpuSmoothingAvailable=false, never a thrown error out of
    // the constructor (mosaicGlController.ts must still get a usable
    // compositor for ordinary, unsmoothed rendering either way).
    let smoothingAvailable = false;
    try {
      smoothingAvailable = decideSmoothingPath(this.probeSmoothingCapabilities()) === "gpu";
      if (smoothingAvailable) this.setUpSmoothingPrograms();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[mosaic-gl] GPU field-smoothing unavailable, using the CPU fallback: ${(err as Error).message}`);
      smoothingAvailable = false;
    }
    this.gpuSmoothingAvailable = smoothingAvailable;

    if (this.isWebGL2) {
      this.timerExt = gl.getExtension("EXT_disjoint_timer_query_webgl2") as DisjointTimerExt | null;
    } else {
      this.timerExt = gl.getExtension("EXT_disjoint_timer_query") as DisjointTimerExt | null;
    }
  }

  private mustGetUniform(program: WebGLProgram, name: string): WebGLUniformLocation {
    const loc = this.gl.getUniformLocation(program, name);
    if (!loc) throw new Error(`uniform ${name} not found (dead-code-eliminated by the GLSL compiler, or a real bug)`);
    return loc;
  }

  private createPaletteTexture(lut: Uint8ClampedArray, size: number, filter: number): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture();
    if (!tex) throw new Error("gl.createTexture failed");
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, lut);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  // ==================== GPU smoothing: capability probe ====================

  /** Empirically probes float/half-float render-target support: creates a
   * tiny (4x4) texture in the target format, attaches it to a scratch FBO,
   * and checks `checkFramebufferStatus` — the real authority, since a
   * browser can report the relevant extension(s) present and still fail
   * this on a specific device/driver. Cleans up the scratch texture/FBO
   * either way. Pure GL-probing glue; the actual gpu-vs-cpu-fallback
   * DECISION over these booleans is mosaicGlCapabilities.ts's pure,
   * unit-tested `decideSmoothingPath`. */
  private probeSmoothingCapabilities(): SmoothingCapabilities {
    const gl = this.gl;
    if (this.isWebGL2) {
      const gl2 = gl as WebGL2RenderingContext;
      const ext = gl2.getExtension("EXT_color_buffer_float");
      const hasFloatRenderTarget = !!ext;
      const fboComplete = hasFloatRenderTarget && this.probeFboCompleteness();
      return { hasWebGL2: true, hasFloatRenderTarget, hasHalfFloatTexture: true, fboComplete };
    }
    this.halfFloatExt = gl.getExtension("OES_texture_half_float") as HalfFloatExt | null;
    const hasHalfFloatTexture = !!this.halfFloatExt;
    const colorBufExt = gl.getExtension("EXT_color_buffer_half_float");
    const hasFloatRenderTarget = !!colorBufExt;
    const fboComplete = hasHalfFloatTexture && hasFloatRenderTarget && this.probeFboCompleteness();
    return { hasWebGL2: false, hasFloatRenderTarget, hasHalfFloatTexture, fboComplete };
  }

  private probeFboCompleteness(): boolean {
    const gl = this.gl;
    const tex = gl.createTexture();
    const fbo = gl.createFramebuffer();
    if (!tex || !fbo) {
      if (tex) gl.deleteTexture(tex);
      if (fbo) gl.deleteFramebuffer(fbo);
      return false;
    }
    try {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.allocateFloatTexture(4, 4);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return status === gl.FRAMEBUFFER_COMPLETE;
    } catch {
      return false;
    } finally {
      gl.deleteTexture(tex);
      gl.deleteFramebuffer(fbo);
    }
  }

  /** Allocates (no pixel data — `null`) the CURRENTLY BOUND TEXTURE_2D as an
   * empty render target of the appropriate float/half-float format for
   * whichever API this context is: WebGL2's sized `RGBA16F`/`HALF_FLOAT`,
   * or WebGL1's unsized `RGBA` + `this.halfFloatExt`'s own `HALF_FLOAT_OES`
   * type constant (a DIFFERENT numeric value from WebGL2's core
   * `HALF_FLOAT` — not interchangeable, hence branching on the actual
   * extension object rather than a shared constant). RGBA (not a single-
   * channel format): WebGL1 has no renderable single-channel float format;
   * keeping both API paths on the same 4-channel layout (value in R, G/B/A
   * unused) also means mosaicGlSmoothShader.ts's shaders never need to
   * know which API they're running under. */
  private allocateFloatTexture(width: number, height: number): void {
    const gl = this.gl;
    if (this.isWebGL2) {
      const gl2 = gl as WebGL2RenderingContext;
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA16F, width, height, 0, gl2.RGBA, gl2.HALF_FLOAT, null);
    } else {
      if (!this.halfFloatExt) throw new Error("allocateFloatTexture: WebGL1 requires the OES_texture_half_float extension");
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, this.halfFloatExt.HALF_FLOAT_OES, null);
    }
  }

  // ==================== GPU smoothing: program setup ====================

  private setUpSmoothingPrograms(): void {
    const gl = this.gl;
    // UNFLIPPED_VERTEX_SHADER_SOURCE, not the display program's own
    // VERTEX_SHADER_SOURCE — see that constant's doc comment
    // (mosaicGlSmoothShader.ts) for why these FBO ping-pong passes need a
    // different v_uv convention than the display pass.
    this.despeckleProgram = compileProgram(gl, UNFLIPPED_VERTEX_SHADER_SOURCE, buildDespeckleFragmentShaderSource());
    this.blurProgram = compileProgram(gl, UNFLIPPED_VERTEX_SHADER_SOURCE, buildBlurFragmentShaderSource());

    this.aPositionDespeckle = gl.getAttribLocation(this.despeckleProgram, "a_position");
    this.aPositionBlur = gl.getAttribLocation(this.blurProgram, "a_position");

    this.uDespeckleRawTex = this.mustGetUniform(this.despeckleProgram, "u_rawTex");
    this.uDespeckleTexSize = this.mustGetUniform(this.despeckleProgram, "u_texSize");
    this.uDespeckleFloorNorm = this.mustGetUniform(this.despeckleProgram, "u_floorNorm");
    this.uDespeckleFillNorm = this.mustGetUniform(this.despeckleProgram, "u_fillNorm");
    this.uDespeckleEnabled = this.mustGetUniform(this.despeckleProgram, "u_despeckleEnabled");
    this.uApplyFill = this.mustGetUniform(this.despeckleProgram, "u_applyFill");

    this.uBlurSrc = this.mustGetUniform(this.blurProgram, "u_src");
    this.uBlurTexelStep = this.mustGetUniform(this.blurProgram, "u_texelStep");
    this.uBlurWeights = Array.from({ length: BLUR_TAP_COUNT }, (_, i) => this.mustGetUniform(this.blurProgram!, `u_w${i}`));
  }

  private ensurePingBuffers(width: number, height: number): void {
    if (this.pingDims && this.pingDims.width === width && this.pingDims.height === height) return;
    this.disposePingBuffers();
    const gl = this.gl;
    const textures: WebGLTexture[] = [];
    const fbos: WebGLFramebuffer[] = [];
    for (let i = 0; i < 2; i++) {
      const tex = gl.createTexture();
      const fbo = gl.createFramebuffer();
      if (!tex || !fbo) throw new Error("gl.createTexture/createFramebuffer failed");
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.allocateFloatTexture(width, height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      textures.push(tex);
      fbos.push(fbo);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.pingTextures = textures as [WebGLTexture, WebGLTexture];
    this.pingFbos = fbos as [WebGLFramebuffer, WebGLFramebuffer];
    this.pingDims = { width, height };
  }

  private disposePingBuffers(): void {
    const gl = this.gl;
    if (this.pingTextures) for (const t of this.pingTextures) gl.deleteTexture(t);
    if (this.pingFbos) for (const f of this.pingFbos) gl.deleteFramebuffer(f);
    this.pingTextures = null;
    this.pingFbos = null;
    this.pingDims = null;
  }

  private drawFullscreenQuad(program: WebGLProgram, aPosition: number): void {
    const gl = this.gl;
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** Runs the despeckle + separable-exact-Gaussian passes on the currently-
   * uploaded raw texture (setRawTexture()), leaving the result available
   * for the next render() call to sample instead of the raw texture. Call
   * whenever the raw texture OR the despeckle/sigma toggles change — NOT
   * on every render() (a pure pan/zoom redraw doesn't need to re-run this;
   * mosaicGlController.ts keeps that same call-frequency discipline the
   * CPU-worker path always had). No-ops (clearing any previous smoothed
   * result) if `!despeckle && sigma <= 0` — render() then falls back to
   * the raw texture directly, an exact pass-through matching the "sigma=off
   * bypasses the blur passes, preserving hard-nodata behavior" requirement.
   * No-ops entirely if `!gpuSmoothingAvailable` or no raw texture is
   * uploaded yet — the caller (mosaicGlController.ts) is expected to check
   * `gpuSmoothingAvailable` itself before ever reaching for this method,
   * but this stays defensive rather than throwing either way.
   *
   * Pass sequence (fieldSmooth()'s own branching, reproduced on the GPU):
   *  - despeckle=false, sigma<=0: no passes at all.
   *  - ANY despeckle||sigma>0: pass A (despeckle+prep) always runs first,
   *    rawTexture -> ping[0], with u_despeckleEnabled/u_applyFill set
   *    independently per fieldSmooth()'s own two independent conditions.
   *  - sigma>0 additionally: pass B (horizontal blur) ping[0] -> ping[1],
   *    then pass C (vertical blur) ping[1] -> ping[0] (reused — pass B
   *    already consumed ping[0]'s despeckle output by the time pass C
   *    writes back into it). Final result is always ping[0] either way.
   *
   * REGRESSION NOTE (the "GPU path renders empty" bug): every pass here
   * MUST compile against mosaicGlSmoothShader.ts's
   * UNFLIPPED_VERTEX_SHADER_SOURCE, never fusionShader.ts's
   * VERTEX_SHADER_SOURCE (used by the DISPLAY program only). The display
   * shader's v_uv is deliberately Y-flipped to match fusionGeo.ts's
   * top-left-origin window convention, which is safe there because it only
   * ever reads through v_uv — its output goes straight to the canvas via
   * rasterization, never back through a sampler. These FBO passes instead
   * SAMPLE their input via v_uv while the GPU WRITES their output at literal
   * (unflipped) window/framebuffer coordinates, so reusing the flipped
   * vertex shader here made every pass write `output.texel(x, y) =
   * input.texel(x, height-1-y)` — a real Y-flip on every pass. Composed
   * across the pipeline (1 pass for despeckle-only, 3 for despeckle+blur),
   * the flip parity came out odd for EVERY non-bypass configuration, so the
   * final smoothedTexture was always vertically mirrored relative to
   * rawTexture — render()'s uvXform-based sampling (which matches
   * rawTexture's own un-flipped, texImage2D-upload addressing, proven
   * correct by the CRISP/bypass path) then sampled the mirrored half of the
   * data, which is empty for most viewports. Despeckle's own logic, the FBO
   * ping-pong mechanics, the texture upload, and the display shader were
   * never actually broken — only the vertex shader shared between the
   * write-then-read-as-input passes and the read-only display pass was
   * wrong for the former. */
  smoothOnGpu(despeckle: boolean, sigma: number): void {
    this.pollPendingTimerQuery();
    if (!this.gpuSmoothingAvailable || !this.despeckleProgram || !this.blurProgram || !this.rawTexture || !this.rawDims) {
      this.smoothedTexture = null;
      return;
    }
    if (!despeckle && sigma <= 0) {
      this.smoothedTexture = null;
      return;
    }

    const gl = this.gl;
    const { width, height } = this.rawDims;
    this.ensurePingBuffers(width, height);
    const pingTex = this.pingTextures!;
    const pingFbo = this.pingFbos!;

    const timerQuery = this.beginGpuTimer();
    const wallClockStart = timerQuery ? 0 : performance.now();

    gl.viewport(0, 0, width, height);

    // Pass A: despeckle + no-echo-as-low prep.
    gl.bindFramebuffer(gl.FRAMEBUFFER, pingFbo[0]);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.rawTexture);
    gl.useProgram(this.despeckleProgram);
    gl.uniform1i(this.uDespeckleRawTex, 0);
    gl.uniform2f(this.uDespeckleTexSize, width, height);
    gl.uniform1f(this.uDespeckleFloorNorm, PRECIP_FLOOR_RAW / 255);
    gl.uniform1f(this.uDespeckleFillNorm, NO_ECHO_FILL_NORM);
    gl.uniform1i(this.uDespeckleEnabled, despeckle ? 1 : 0);
    gl.uniform1i(this.uApplyFill, sigma > 0 ? 1 : 0);
    this.drawFullscreenQuad(this.despeckleProgram, this.aPositionDespeckle);

    if (sigma > 0) {
      const weights = padKernelToBlurTapCount(gaussianKernel1D(sigma));

      // Pass B: horizontal blur, ping[0] -> ping[1].
      gl.bindFramebuffer(gl.FRAMEBUFFER, pingFbo[1]);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, pingTex[0]);
      gl.useProgram(this.blurProgram);
      gl.uniform1i(this.uBlurSrc, 0);
      gl.uniform2f(this.uBlurTexelStep, 1 / width, 0);
      this.setBlurWeights(weights);
      this.drawFullscreenQuad(this.blurProgram, this.aPositionBlur);

      // Pass C: vertical blur, ping[1] -> ping[0] (reused: pass B already
      // consumed ping[0]'s content by the time this overwrites it).
      gl.bindFramebuffer(gl.FRAMEBUFFER, pingFbo[0]);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, pingTex[1]);
      gl.uniform1i(this.uBlurSrc, 0);
      gl.uniform2f(this.uBlurTexelStep, 0, 1 / height);
      this.setBlurWeights(weights);
      this.drawFullscreenQuad(this.blurProgram, this.aPositionBlur);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.smoothedTexture = pingTex[0];

    const meta = `despeckle=${despeckle},sigma=${sigma},${width}x${height}`;
    if (timerQuery) {
      this.endGpuTimer(timerQuery, meta);
    } else {
      // No EXT_disjoint_timer_query(_webgl2) available — fall back to a
      // synchronous wall-clock+finish() measurement. Acceptable ONLY
      // because this method runs on data/toggle changes, not on every
      // render() (see this method's own doc comment) — the "normal render
      // loop" (doRender()'s per-frame draw) never gets a finish() call.
      gl.finish();
      recordDuration("field-smooth-gpu", performance.now() - wallClockStart, `${meta},no-timer-query`);
    }
  }

  private setBlurWeights(weights: Float32Array): void {
    const gl = this.gl;
    for (let i = 0; i < BLUR_TAP_COUNT; i++) gl.uniform1f(this.uBlurWeights[i], weights[i]);
  }

  // ---- GPU timer queries (EXT_disjoint_timer_query(_webgl2)) ----

  private beginGpuTimer(): WebGLQuery | null {
    if (!this.timerExt) return null;
    const gl = this.gl;
    try {
      if (this.isWebGL2) {
        const gl2 = gl as WebGL2RenderingContext;
        const query = gl2.createQuery();
        if (!query) return null;
        gl2.beginQuery(this.timerExt.TIME_ELAPSED_EXT, query);
        return query;
      }
      const query = this.timerExt.createQueryEXT?.();
      if (!query) return null;
      this.timerExt.beginQueryEXT?.(this.timerExt.TIME_ELAPSED_EXT, query);
      return query;
    } catch {
      return null; // timer queries are a perf-measurement nicety — never let a failure here break rendering
    }
  }

  private endGpuTimer(query: WebGLQuery, meta: string): void {
    const gl = this.gl;
    try {
      if (this.isWebGL2) (gl as WebGL2RenderingContext).endQuery(this.timerExt!.TIME_ELAPSED_EXT);
      else this.timerExt!.endQueryEXT?.(this.timerExt!.TIME_ELAPSED_EXT);
      // Not available yet (queries are asynchronous by design) — polled on
      // the NEXT smoothOnGpu() call instead, non-blocking (see
      // pollPendingTimerQuery below). Only one query is ever in flight;
      // this method only runs after the previous one was already polled.
      this.pendingQuery = query;
      this.pendingQueryMeta = meta;
    } catch {
      /* best-effort */
    }
  }

  /** Non-blocking check for a timer query issued by a PREVIOUS
   * smoothOnGpu() call — by the time the NEXT call happens (always at
   * least one animation frame later), the result is essentially always
   * ready, so this never needs its own polling loop. Discards the result
   * if `GPU_DISJOINT_EXT` reports true (a GPU reset/frequency change
   * during the measurement — the spec's own signal that the timing is
   * meaningless), per the extension's documented usage pattern. */
  private pollPendingTimerQuery(): void {
    if (!this.pendingQuery || !this.timerExt) return;
    const gl = this.gl;
    const query = this.pendingQuery;
    const meta = this.pendingQueryMeta ?? "";
    try {
      let available: unknown;
      if (this.isWebGL2) {
        const gl2 = gl as WebGL2RenderingContext;
        available = gl2.getQueryParameter(query, gl2.QUERY_RESULT_AVAILABLE);
      } else {
        available = this.timerExt.getQueryObjectEXT?.(query, this.timerExt.QUERY_RESULT_AVAILABLE_EXT ?? 0);
      }
      if (!available) return; // still pending -- check again next call
      const disjoint = gl.getParameter(this.timerExt.GPU_DISJOINT_EXT) as boolean;
      if (!disjoint) {
        let elapsedNs: number;
        if (this.isWebGL2) {
          const gl2 = gl as WebGL2RenderingContext;
          elapsedNs = gl2.getQueryParameter(query, gl2.QUERY_RESULT) as number;
        } else {
          elapsedNs = this.timerExt.getQueryObjectEXT?.(query, this.timerExt.QUERY_RESULT_EXT ?? 0) as number;
        }
        recordDuration("field-smooth-gpu", elapsedNs / 1e6, meta);
      }
    } catch {
      /* best-effort */
    } finally {
      if (this.isWebGL2) (gl as WebGL2RenderingContext).deleteQuery(query);
      else this.timerExt.deleteQueryEXT?.(query);
      this.pendingQuery = null;
      this.pendingQueryMeta = null;
    }
  }

  // ==================== main display pass ====================

  /** Uploads the current mosaic frame's TRUE raw decoded grid — always
   * NEAREST/CLAMP_TO_EDGE (same reasoning as fusionCompositor.ts's station
   * textures: these are quantized measurement bytes, and mosaicGlShader.ts's
   * kernel does its own data-space resampling, so hardware texture
   * filtering must stay off). When `gpuSmoothingAvailable`, the caller
   * uploads the LITERAL decoded grid here and calls smoothOnGpu()
   * separately; when it isn't, the caller instead uploads whatever
   * mosaicGlFieldSmoothing.ts's CPU worker already produced (unchanged
   * from before this feature existed) — this method's contract doesn't
   * change either way, it just displays whatever it's given. */
  setRawTexture(raw: Uint8Array, width: number, height: number): void {
    const gl = this.gl;
    let tex = this.rawTexture;
    if (!tex) {
      const created = gl.createTexture();
      if (!created) throw new Error("gl.createTexture failed");
      tex = created;
      this.rawTexture = tex;
    }
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, width, height, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, raw);
    this.rawDims = { width, height };
    this.smoothedTexture = null; // a new raw upload invalidates any previous GPU-smoothed result until smoothOnGpu() runs again
  }

  isLost(): boolean {
    return this.lost;
  }

  /** Renders whichever texture is current — the GPU-smoothed result
   * (smoothOnGpu(), if it produced one) or the raw texture directly
   * (smoothing bypassed, or the CPU-fallback path already pre-smoothed
   * what setRawTexture() received) — resampled per `sampling`/`floorOrder`/
   * `floorRaw`/`floorHardness` and colorized per `palette`, into
   * `this.canvas` at `outputWidth`x`outputHeight`. `uvXform` maps the
   * caller's window UV into this compositor's texture UV — fusionGeo.ts's
   * stationUvTransform, computed by the caller from the current viewport
   * bounds against the mosaic frame's own bbox. No-ops if no raw texture
   * has been uploaded yet. */
  render(
    uvXform: UvTransform,
    outputWidth: number,
    outputHeight: number,
    sampling: SamplingMode,
    floorOrder: FloorOrder,
    floorRaw: number,
    floorHardness: FloorHardness,
    palette: PaletteMode,
  ): void {
    const gl = this.gl;
    if (!this.rawTexture || !this.rawDims) return;
    if (this.canvas.width !== outputWidth || this.canvas.height !== outputHeight) {
      this.canvas.width = outputWidth;
      this.canvas.height = outputHeight;
    }
    gl.viewport(0, 0, outputWidth, outputHeight);
    gl.useProgram(this.program);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.uniform4f(this.uUvXform, uvXform.scaleX, uvXform.scaleY, uvXform.offsetX, uvXform.offsetY);
    gl.uniform2f(this.uTexSize, this.rawDims.width, this.rawDims.height);
    gl.uniform1i(this.uSampling, SAMPLING_MODE_INDEX[sampling]);
    gl.uniform1i(this.uFloorOrder, FLOOR_ORDER_INDEX[floorOrder]);
    gl.uniform1f(this.uFloor, floorRaw / 255);
    gl.uniform1i(this.uFloorHard, FLOOR_HARDNESS_INDEX[floorHardness]);
    gl.uniform1f(this.uFeatherWidth, FEATHER_WIDTH_NORM);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.smoothedTexture ?? this.rawTexture);
    gl.uniform1i(this.uTex, 0);

    const paletteTex = palette === "banded" ? this.bandedPaletteTexture : this.continuousPaletteTexture;
    const paletteSize = palette === "banded" ? BANDED_LUT_SIZE : CONTINUOUS_LUT_SIZE;
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, paletteTex);
    gl.uniform1i(this.uPalette, 1);
    gl.uniform1f(this.uPaletteSize, paletteSize);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.enableVertexAttribArray(this.aPositionMain);
    gl.vertexAttribPointer(this.aPositionMain, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteBuffer(this.positionBuffer);
    gl.deleteTexture(this.bandedPaletteTexture);
    gl.deleteTexture(this.continuousPaletteTexture);
    if (this.rawTexture) gl.deleteTexture(this.rawTexture);
    if (this.despeckleProgram) gl.deleteProgram(this.despeckleProgram);
    if (this.blurProgram) gl.deleteProgram(this.blurProgram);
    this.disposePingBuffers();
    if (this.pendingQuery) {
      if (this.isWebGL2) (gl as WebGL2RenderingContext).deleteQuery(this.pendingQuery);
      else this.timerExt?.deleteQueryEXT?.(this.pendingQuery);
    }
  }
}
